const BaseConfigService = require('./BaseConfigService');
const { escapeHtml, sanitizeTheme, sanitizeUrl, sanitizeApiData } = require('../../security');

/**
 * Main SDK Configuration Service
 *
 * Handles core SDK configuration separate from payment provider configs
 * Responsibilities:
 * - SDK initialization parameters
 * - UI configuration
 * - Language and localization
 * - Theme and styling
 * - General SDK behavior
 */
class MainConfigService extends BaseConfigService {
  /**
   * Get default main SDK configuration
   */
  _getDefaultConfig() {
    return {
      // Core SDK settings
      merchant_id: '',
      session_id: null,
      apiKey: '',
      selector: '',
      lang: 'en',
      render: true,
      displayMode: 'column',
      splitOrder: null,
      splitReversed: false,
      shadowRootClosed: true,
      captureAll: false,
      mobilePayOtpLength: 1,
      mobilePayOtpCooldownTime: 60,

      // Payment configuration
      amount: null,
      formsOfPayment: null,
      payment_methods: null,
      payment_type: null,
      cancel_url: null,
      // True when the Ottu PG configuration behind the session is in sandbox
      // mode. Returned at the root of the checkout init response.
      is_sandbox: false,

      // UI configuration
      payButtonText: {
        en: 'Pay',
        ar: 'إدفع',
      },
      theme: {},
      displayRejectButton: false,
      rejectButtonConfig: {
        text: {
          en: 'Reject',
          ar: 'الغاء',
        },
        renderAsLink: false,
      },
      displayPciLogos: false,
      termsAndConditions: {
        enabled: false,
        required: true,
        url: null,
      },
      customer_phone: null,

      // Preload data
      setupPreload: null,

      // Friend Pay configuration
      shareText: null,
    };
  }

  /**
   * Validate required fields for SDK initialization
   */
  _validateInitialization() {
    return this._validateRequiredFields();
  }

  /**
   * Apply general SDK configuration
   */
  _applyConfig(config) {
    const safeConfig = this._sanitizeConfig(config);

    // Direct property mappings
    const directMappings = [
      'formsOfPayment',
      'lang',
      'session_id',
      'merchant_id',
      'selector',
      'apiKey',
      'render',
      'displayMode',
      'splitOrder',
      'splitReversed',
      'customer_phone',
      'theme',
      'mobilePayOtpLength',
      'setupPreload',
      'displayRejectButton',
      'rejectButtonConfig',
      'termsAndConditions',
      'cancel_url',
      'payment_type',
      'payment_methods',
      'payment_services',
      'is_sandbox',
      'state',
      'shadowRootClosed',
      'captureAll',
      'mobilePayOtpCooldownTime',
      'googlePayInit',
      'applePayInit',
      '_studioEnabled',
      'cards',
      'payButtonText',
      'displayPciLogos',
      'shareText',
      'sdk_studio',
      'amount',
      'billing',
      'flex_methods',
    ];

    directMappings.forEach((key) => {
      if (typeof safeConfig[key] !== 'undefined') {
        this._config[key] = safeConfig[key];
      } else if (typeof config[key] !== 'undefined') {
        console.warn(
          `[Warning] Configuration key "${key}" was filtered out during sanitization`
        );
      }
    });

    // Warn if config has keys not in directMappings
    Object.keys(config).forEach((key) => {
      if (!directMappings.includes(key) && typeof config[key] !== 'undefined') {
        console.warn(
          `[Warning] Configuration key "${key}" is not in directMappings and will be ignored`
        );
      }
    });
  }

  /**
   * Sanitize configuration
   */
  _sanitizeConfig(config) {
    const sanitized = { ...config };

    if (config.payButtonText && typeof config.payButtonText === 'object') {
      ['en', 'ar'].forEach((lang) => {
        sanitized.payButtonText[lang] = escapeHtml(config.payButtonText[lang]);
      });
    }

    // Sanitize askFriendToPay configuration
    if (config.askFriendToPay && typeof config.askFriendToPay === 'object') {
      sanitized.askFriendToPay = {
        enabled: false,
        shareText: {
          en: this._config.askFriendToPay.shareText.en,
          ar: this._config.askFriendToPay.shareText.ar,
        },
      };

      // Validate enabled flag
      if (typeof config.askFriendToPay.enabled === 'boolean') {
        sanitized.askFriendToPay.enabled = config.askFriendToPay.enabled;
      } else if (config.askFriendToPay.enabled !== undefined) {
        console.warn(
          '[CheckoutSDK][askFriendToPay] Invalid "enabled" value. Expected boolean, received ' +
            typeof config.askFriendToPay.enabled +
            '. Using default: false'
        );
      }

      // Validate and sanitize shareText
      if (
        config.askFriendToPay.shareText &&
        typeof config.askFriendToPay.shareText === 'object'
      ) {
        ['en', 'ar'].forEach((lang) => {
          const text = config.askFriendToPay.shareText[lang];
          if (typeof text === 'string') {
            const trimmed = text.trim();
            if (trimmed.length > 0) {
              sanitized.askFriendToPay.shareText[lang] = escapeHtml(trimmed);
            } else {
              console.warn(
                `[CheckoutSDK][askFriendToPay] Empty shareText.${lang} provided. Using default.`
              );
            }
          } else if (text !== undefined) {
            console.warn(
              `[CheckoutSDK][askFriendToPay] Invalid shareText.${lang} type. Expected string, received ${typeof text}. Using default.`
            );
          }
        });
      } else if (config.askFriendToPay.shareText !== undefined) {
        console.warn(
          '[CheckoutSDK][askFriendToPay] Invalid shareText format. Expected object with en/ar keys. Using defaults.'
        );
      }
    }

    // Validate termsAndConditions
    if (config.termsAndConditions !== undefined && config.termsAndConditions !== null) {
      if (typeof config.termsAndConditions !== 'object' || Array.isArray(config.termsAndConditions)) {
        console.warn('[CheckoutSDK] Invalid termsAndConditions value. Expected object with { enabled, url }, received ' + typeof config.termsAndConditions + '. Using defaults.');
        sanitized.termsAndConditions = { enabled: false, url: null };
      } else {
        const tc = config.termsAndConditions;
        const sanitizedTc = { enabled: false, required: true, url: null };

        if (tc.enabled !== undefined) {
          if (typeof tc.enabled !== 'boolean') {
            console.warn('[CheckoutSDK] Invalid termsAndConditions.enabled value. Expected boolean, received ' + typeof tc.enabled + '. Using default: false');
          } else {
            sanitizedTc.enabled = tc.enabled;
          }
        }

        if (tc.required !== undefined) {
          if (typeof tc.required !== 'boolean') {
            console.warn('[CheckoutSDK] Invalid termsAndConditions.required value. Expected boolean, received ' + typeof tc.required + '. Using default: true');
          } else {
            sanitizedTc.required = tc.required;
          }
        }

        if (tc.url !== undefined && tc.url !== null) {
          if (typeof tc.url === 'string') {
            const safeUrl = sanitizeUrl(tc.url);
            if (safeUrl) {
              sanitizedTc.url = safeUrl;
            } else {
              console.warn('[CheckoutSDK] Invalid termsAndConditions.url. URL did not pass sanitization. Using default: null');
            }
          } else {
            console.warn('[CheckoutSDK] Invalid termsAndConditions.url value. Expected string, received ' + typeof tc.url + '. Using default: null');
          }
        }

        sanitized.termsAndConditions = sanitizedTc;
      }
    }

    // Validate splitOrder
    if (config.splitOrder !== undefined && config.splitOrder !== null) {
      const validSections = ['cardsAndServices', 'methods', 'walletButtons', 'amount', 'cardSave', 'fees', 'buttons'];
      if (typeof config.splitOrder === 'object' && !Array.isArray(config.splitOrder)) {
        const filtered = {};
        Object.entries(config.splitOrder).forEach(([key, value]) => {
          if (validSections.includes(key) && Number.isFinite(value) && Number.isInteger(value)) {
            filtered[key] = value;
          } else {
            console.warn(`[CheckoutSDK] Invalid splitOrder entry: "${key}" => ${value}. Key must be one of [${validSections.join(', ')}] and value must be a finite integer.`);
          }
        });
        sanitized.splitOrder = Object.keys(filtered).length > 0 ? filtered : null;
      } else {
        console.warn('[CheckoutSDK] Invalid splitOrder format. Expected object, received ' + typeof config.splitOrder + '. Using default: null');
        sanitized.splitOrder = null;
      }
    }

    // Validate splitReversed
    if (config.splitReversed !== undefined) {
      if (typeof config.splitReversed !== 'boolean') {
        console.warn('[CheckoutSDK] Invalid splitReversed value. Expected boolean, received ' + typeof config.splitReversed + '. Using default: false');
        sanitized.splitReversed = false;
      }
    }

    // Validate rejectButtonConfig
    if (config.rejectButtonConfig !== undefined) {
      const sanitizedRbc = { text: { en: 'Reject', ar: 'الغاء' }, renderAsLink: false };
      if (typeof config.rejectButtonConfig !== 'object' || Array.isArray(config.rejectButtonConfig)) {
        console.warn('[CheckoutSDK] Invalid rejectButtonConfig value. Expected object. Using default.');
        sanitized.rejectButtonConfig = sanitizedRbc
      } else {
        const rbc = config.rejectButtonConfig;

        if (rbc.text !== undefined) {
          if (typeof rbc.text === 'object' && !Array.isArray(rbc.text)) {
            sanitizedRbc.text = { ...sanitizedRbc.text };
            ['en', 'ar'].forEach((lang) => {
              if (typeof rbc.text[lang] === 'string') {
                sanitizedRbc.text[lang] = escapeHtml(rbc.text[lang]);
              }
            });
          } else {
            console.warn('[CheckoutSDK] Invalid rejectButtonConfig.text. Expected object with en/ar keys. Using default.');
          }
        }

        if (rbc.renderAsLink !== undefined) {
          if (typeof rbc.renderAsLink === 'boolean') {
            sanitizedRbc.renderAsLink = rbc.renderAsLink;
          } else {
            console.warn('[CheckoutSDK] Invalid rejectButtonConfig.renderAsLink. Expected boolean, received ' + typeof rbc.renderAsLink + '. Using default: false');
          }
        }

        sanitized.rejectButtonConfig = sanitizedRbc;
      }
    }

    if (typeof config.theme !== 'undefined') {
      if (config.theme.stcPay && typeof config.theme.stcPay === 'object') {
        config.theme.stcPay['background-color'] =
          config.theme.stcPay.buttonColor;
      }
      if (config.theme.urPay && typeof config.theme.urPay === 'object') {
        config.theme.urPay['background-color'] = config.theme.urPay.buttonColor;
      }

      // Extract and validate responsive breakpoints before sanitizeTheme strips them
      let responsive = undefined;
      if (config.theme.responsive !== undefined) {
        if (config.theme.responsive && typeof config.theme.responsive === 'object' && !Array.isArray(config.theme.responsive)) {
          responsive = {};
          Object.entries(config.theme.responsive).forEach(([key, value]) => {
            const bp = Number(key);
            if (Number.isFinite(bp) && bp > 0 && Number.isInteger(bp)) {
              if (value && typeof value === 'object' && !Array.isArray(value)) {
                responsive[key] = value;
              } else {
                console.warn(`[CheckoutSDK] Invalid responsive breakpoint value for "${key}". Expected object.`);
              }
            } else {
              console.warn(`[CheckoutSDK] Invalid responsive breakpoint key "${key}". Expected positive integer.`);
            }
          });
          if (Object.keys(responsive).length === 0) {
            responsive = undefined;
          }
        } else {
          console.warn('[CheckoutSDK] Invalid theme.responsive format. Expected object.');
        }
      }

      // eslint-disable-next-line no-unused-vars
      const { responsive: _discardedResponsive, ...themeWithoutResponsive } = config.theme;
      sanitized.theme = sanitizeTheme(themeWithoutResponsive);

      // Re-attach validated responsive after sanitization
      if (responsive) {
        sanitized.theme.responsive = responsive;
      }
    }

    if (typeof config.setupPreload !== 'undefined') {
      try {
        sanitized.setupPreload = JSON.parse(
          JSON.stringify(config.setupPreload)
        );
      } catch (error) {
        console.warn('Invalid setupPreload configuration:', error);
        sanitized.setupPreload = null;
      }
    }

    return sanitized;
  }

  /**
   * Validate required fields for SDK initialization
   */
  _validateRequiredFields() {
    const requiredFields = ['selector', 'session_id', 'apiKey', 'merchant_id'];
    const missingFields = requiredFields.filter(
      (field) => !this._config[field]
    );

    if (missingFields.length > 0) {
      console.log(
        `[Error] Failed to initialize SDK, Missing fields: "${missingFields.join(
          ', '
        )}" in Checkout.init()`
      );
      return false;
    }

    return true;
  }

  applyPayload(payload) {
    if (!payload) return;
    const sanitizedMethods = (payload.payment_methods || []).map(sanitizeApiData);
    const sanitizedServices = (payload.payment_services || []).map(sanitizeApiData);
    const sanitizedFlex = (payload.flex_methods || []).map(sanitizeApiData);
    const sanitizedCards = (payload.cards || []).map(sanitizeApiData);
    this.updateConfig({
      payment_methods: sanitizedMethods,
      payment_services: sanitizedServices,
      flex_methods: sanitizedFlex,
      cards: sanitizedCards,
    });
    if (payload.amount) this.updateConfig({ amount: escapeHtml(String(payload.amount)) });
    if (payload.billing) this.updateConfig({ billing: sanitizeApiData(payload.billing) });
  }

  setSDKStudioConfigs(studioConfigs = {}) {
    const initConfigs = studioConfigs.init;

    if (!initConfigs) {
      return;
    }

    const { displayRejectButton } = initConfigs;
    const supportedConfigs = { displayRejectButton };

    this.updateConfig(supportedConfigs);
  }
}

// Export singleton instance
module.exports = new MainConfigService();
